import { defineStore } from 'pinia'
import { reactive } from 'vue'
import { api } from '@/plugins/axios'
import event from '@/plugins/events'
import { formatPhoneNumber } from '@/plugins/util'
import { useSalesStore } from '@/stores/sales'

const storage = window.sessionStorage
const localStorage = window.localStorage

const normalizePhone = (phone) => {
  if (!phone || typeof phone !== 'string') return null
  const digits = formatPhoneNumber(phone.trim())
  return digits || null
}

export const useCustomerStore = defineStore('customer', {
  state: () => ({
    customer: JSON.parse(storage.getItem('Customer') || null),
    searchSelections: [],
    searchResults: [],
    searchCriteria: {
      email: null,
      phone: null,
      first_name: null,
      last_name: null,
      order_id: null
    },
    callSessionId: storage.getItem('CallSessionId'),
    chatVisitorId: storage.getItem('ChatVisitorId'),
    chatHostname: storage.getItem('ChatHostname'),
    ani: storage.getItem('ANI'),
    dnis: storage.getItem('DNIS'),
    queue: storage.getItem('Queue'),
    // Channel of the leg this screen pop came from (e.g. 'retell', 'botpress'/'aichat'/
    // 'chat', 'webex', 'cash') — lets a fresh-ticket creation pick the right Zendesk
    // comment template (phone- vs chat-shaped) instead of always assuming phone.
    sessionSource: storage.getItem('SessionSource'),
    recentOrder: storage.getItem('RecentOrder'),
    retention: JSON.parse(storage.getItem('Retention') || null),
    requireReasonForContact: JSON.parse(storage.getItem('RequireReasonForContact') || null),
    reasonForContact: storage.getItem('ReasonForContact'),
    identified: JSON.parse(storage.getItem('Identified') || null),
    aiSummary: storage.getItem('AiSummary'),
    screenPopId: storage.getItem('ScreenPopId'),
    screenPopCreationPromise: null,
    subscriptions: JSON.parse(storage.getItem('CustomerSubscriptions') || null),
    subscriptionsLoaded: JSON.parse(storage.getItem('SubsLoaded') || false),
    productLineCategories: [
      'Tech Support',
      'Device Optimization',
      'Data Protection',
      'Identity Protection',
      'Data Broker Removal'
    ],
    // Reference data shared across all customers/agents — cached in localStorage, not per-customer state
    contactReasons: JSON.parse(localStorage.getItem('ContactReasons') || '[]'),
    askedUpdatePhone: JSON.parse(storage.getItem('AskedUpdatePhone') || false),
    notices: (() => {
      const stored = JSON.parse(storage.getItem('CustomerNotices') || '[]')
      return stored
    })(),
    noticeIdCounter: (() => {
      const stored = JSON.parse(storage.getItem('CustomerNotices') || '[]')
      return stored.length ? Math.max(...stored.map((n) => n.id || 0)) : 0
    })(),
    calloutEventIds: {},
    autoOpenedCallouts: JSON.parse(storage.getItem('AutoOpenedCallouts') || '[]'),
    // AI Customer Briefing (CASH-1044): transient fetched context + the fire-once set
    // (keyed by screen_pop_id) so the briefing opens exactly once per transferred call
    // and never re-fires for a returning customer. Survives refresh via sessionStorage.
    briefingContext: null,
    briefingShownFor: JSON.parse(storage.getItem('BriefingShownFor') || '[]'),
    customerStats: JSON.parse(storage.getItem('CustomerStats') || '{}'),
    selectedSubscription: JSON.parse(storage.getItem('SelectedSubscription') || null),
    // Per-partner (hp/acronis/lenovo) detection, resolved once per profile load in
    // customerSupport.js from the same cash_callout_configs trigger_conditions that drive the
    // partner_no_upsell_* banners -- { [partner]: { isCustomer: bool, subscriptionKeys: string[] } }.
    // hpPartnerLogic.js/acronisPartnerLogic.js/lenovoPartnerLogic.js read this instead of owning
    // their own hardcoded SKU/DNIS predicate, so an admin editing a partner's trigger_conditions in
    // the callout admin UI changes every consumer (banner, subscription row menu, sales catalog,
    // SKU label, activation product_brand) at once, not just the banner.
    partnerFlags: JSON.parse(storage.getItem('PartnerFlags') || '{}')
  }),
  actions: {
    getStickyOrderUrl(order_id) {
      return `https://${import.meta.env.VITE_STICKY_SUB_DOMAIN}.sticky.io/admin/orders.php?show_details=show_details&show_folder=view_all&fromPost=1&sequence=1&show_by_id=${order_id}`
    },
    setScreenPopCreationPromise(promise) {
      this.screenPopCreationPromise = promise
    },
    async setCustomer(customer, foundFromPhone, fromTicketPop) {
      this.setAskedUpdatePhone()
      // Wait for any in-flight screen pop creation to settle so screenPopId is set
      // before we decide whether to create a new record or update the existing one.
      if (this.screenPopCreationPromise) {
        await this.screenPopCreationPromise.catch(() => {})
      }
      if (!customer) {
        this.customer = null
        storage.removeItem('Customer')
        return
      }
      this.customer = Object.assign({}, customer)
      storage.setItem('Customer', JSON.stringify(this.customer))
      if (this.getCallSessionId || this.getChatVisitorId || foundFromPhone || fromTicketPop) {
        try {
          const logData = { customer_dim_key: customer.customer_dim_key }
          if (customer.email) logData.customer_email = customer.email
          if (this.getCallSessionId) logData.call_session_id = this.getCallSessionId
          if (foundFromPhone) logData.found_from_phone = true
          if (fromTicketPop) logData.ticket_id = fromTicketPop
          if (this.getScreenPopId) logData.id = this.getScreenPopId
          const { id } = await api.post('/customers/screen_pop', logData)
          if (!this.getScreenPopId) this.setScreenPopId(id)
        } catch (e) {
          event.error(e)
        }
      }
    },
    setAskedUpdatePhone(val) {
      if (val) {
        this.askedUpdatePhone = true
        storage.setItem('AskedUpdatePhone', true)
      } else {
        storage.removeItem('AskedUpdatePhone')
        this.askedUpdatePhone = false
      }
    },
    setZendeskId(id) {
      if (this.customer) this.customer.zendesk_id = id
      if (id) storage.setItem('Customer', JSON.stringify(this.customer))
    },
    setRecentOrder(id) {
      this.recentOrder = id
      id ? storage.setItem('RecentOrder', id) : storage.removeItem('RecentOrder')
    },
    setScreenPopId(id) {
      this.screenPopId = id
      id ? storage.setItem('ScreenPopId', this.screenPopId) : storage.removeItem('ScreenPopId')
    },
    setRequireReasonForContact(val) {
      if (val && this.reasonForContact) return
      this.requireReasonForContact = val
      val
        ? storage.setItem('RequireReasonForContact', this.requireReasonForContact)
        : storage.removeItem('RequireReasonForContact')
    },
    setReasonForContact(val) {
      this.reasonForContact = val
      val ? storage.setItem('ReasonForContact', val) : storage.removeItem('ReasonForContact')
    },
    setIdentified(val) {
      this.identified = val
      // val can legitimately be `false` (not identified) — only clear on null/undefined
      val === undefined || val === null
        ? storage.removeItem('Identified')
        : storage.setItem('Identified', JSON.stringify(val))
    },
    setAiSummary(val) {
      this.aiSummary = val
      val ? storage.setItem('AiSummary', val) : storage.removeItem('AiSummary')
    },
    setContactReasons(reasons) {
      this.contactReasons = reasons || []
      this.contactReasons.length
        ? localStorage.setItem('ContactReasons', JSON.stringify(this.contactReasons))
        : localStorage.removeItem('ContactReasons')
    },
    setSearchSelections(data) {
      this.searchSelections = data
    },
    setSearchResults(data) {
      this.searchResults = data
    },
    setSearchCriteria(data) {
      this.searchCriteria = Object.assign({}, data)
    },
    setCallSessionId(id) {
      this.callSessionId = id
      id ? storage.setItem('CallSessionId', id) : storage.removeItem('CallSessionId')
    },
    setChatVisitorId(id) {
      this.chatVisitorId = id
      id ? storage.setItem('ChatVisitorId', id) : storage.removeItem('ChatVisitorId')
    },
    setChatHostname(id) {
      this.chatHostname = id
      id ? storage.setItem('ChatHostname', id) : storage.removeItem('ChatHostname')
    },
    setSessionSource(source) {
      this.sessionSource = source
      source ? storage.setItem('SessionSource', source) : storage.removeItem('SessionSource')
    },
    setRetention(data) {
      if (data) {
        this.retention = Object.assign({}, data)
        storage.setItem('Retention', JSON.stringify(this.retention))
      } else {
        this.retention = null
        storage.removeItem('Retention')
      }
    },
    setSelectedSubscription(sub) {
      this.selectedSubscription = sub ? Object.assign({}, sub) : null
      storage.setItem('SelectedSubscription', JSON.stringify(this.selectedSubscription))
    },
    setSubscriptions(subs) {
      this.subscriptions = subs
      if (subs) {
        storage.setItem('CustomerSubscriptions', JSON.stringify(subs))
        this.subscriptionsLoaded = true
        storage.setItem('SubsLoaded', this.subscriptionsLoaded)
      } else {
        storage.removeItem('CustomerSubscriptions')
        storage.removeItem('SubsLoaded')
        this.subscriptionsLoaded = false
      }
    },
    // Sets this tab's local telephony/session state from URL query params. The
    // screen pop record itself (creation, and pre-populating a new leg's data from
    // whichever leg it was transferred from) is handled server-side via
    // screen_pop_id on the create call — see screenPopRoute in cash-server. This
    // action only drives local UI (e.g. SessionDetails.vue's call session/queue
    // display); it no longer writes to the server itself.
    setQuery(query) {
      if (!query) {
        this.ani = null
        this.dnis = null
        this.queue = null
        this.setChatVisitorId()
        this.setChatHostname()
        this.setSessionSource()
        return this.setCallSessionId()
      }
      let sessionId = query.sessionId || query.callSessionId || query.session_id
      if (sessionId && sessionId != '') {
        this.setCallSessionId(sessionId)
        this.setRequireReasonForContact(true)
      }
      if (query.session_source && query.session_source != '') {
        this.setSessionSource(query.session_source)
      }
      if (query.chatVisitorId && query.chatVisitorId != '') {
        this.setChatVisitorId(query.chatVisitorId)
        this.setRequireReasonForContact(true)
      }
      if (query.hostname && query.hostname != '') {
        this.setChatHostname(query.hostname)
        this.setRequireReasonForContact(true)
      }
      if (query.phone && query.phone != '') {
        this.ani = normalizePhone(query.phone)
        this.setRequireReasonForContact(true)
      }
      let dnis = query.DNIS || query.dnis
      if (dnis && dnis != '') {
        this.dnis = formatPhoneNumber(dnis)
      }
      if (query.queue && query.queue != '') {
        this.queue = query.queue
      }
    },
    getStopSubscription(stop_subscription_id) {
      if (!stop_subscription_id) return
      const [order_id, product_id] = stop_subscription_id.split('.')
      return this.subscriptions.find(
        (s) => s.product_id == product_id && (s.last_order_id == order_id || s.order_id == order_id)
      )
    },
    setNotices(notices) {
      this.notices = notices || []
      // Sync noticeIdCounter to max ID in notices to prevent ID conflicts
      if (this.notices.length) {
        const maxId = Math.max(...this.notices.map((n) => n.id || 0))
        this.noticeIdCounter = maxId
        storage.setItem('CustomerNotices', JSON.stringify(this.notices))
      } else {
        this.noticeIdCounter = 0
        storage.removeItem('CustomerNotices')
      }
    },
    pushNotice(notice) {
      if (!notice) return
      const id = ++this.noticeIdCounter
      const newNotice = {
        id,
        type: notice.type || 'info',
        title: notice.title || null,
        titleHTML: notice.titleHTML || null,
        text: notice.text || null,
        textHTML: notice.textHTML || null,
        link: notice.link || null
      }
      this.notices = [...this.notices, newNotice]
      storage.setItem('CustomerNotices', JSON.stringify(this.notices))
    },
    removeNotice(id) {
      this.notices = this.notices.filter((n) => n.id !== id)
      if (this.notices.length) {
        storage.setItem('CustomerNotices', JSON.stringify(this.notices))
      } else {
        storage.removeItem('CustomerNotices')
      }
    },
    clearNotices() {
      this.notices = []
      this.noticeIdCounter = 0
      this.calloutEventIds = {}
      this.autoOpenedCallouts = []
      storage.removeItem('CustomerNotices')
      storage.removeItem('AutoOpenedCallouts')
    },
    markCalloutAutoOpened(calloutId) {
      if (!this.autoOpenedCallouts.includes(calloutId)) {
        this.autoOpenedCallouts = [...this.autoOpenedCallouts, calloutId]
        storage.setItem('AutoOpenedCallouts', JSON.stringify(this.autoOpenedCallouts))
      }
    },
    hasCalloutBeenAutoOpened(calloutId) {
      return this.autoOpenedCallouts.includes(calloutId)
    },
    setBriefingContext(ctx) {
      this.briefingContext = ctx || null
    },
    markBriefingShown(id) {
      if (id == null) return
      const key = String(id)
      if (!this.briefingShownFor.includes(key)) {
        this.briefingShownFor = [...this.briefingShownFor, key]
        storage.setItem('BriefingShownFor', JSON.stringify(this.briefingShownFor))
      }
    },
    hasBriefingBeenShown(id) {
      return id != null && this.briefingShownFor.includes(String(id))
    },
    setCalloutEventIds(ids) {
      this.calloutEventIds = ids || {}
    },
    setPartnerFlags(flags) {
      this.partnerFlags = flags || {}
      Object.keys(this.partnerFlags).length
        ? storage.setItem('PartnerFlags', JSON.stringify(this.partnerFlags))
        : storage.removeItem('PartnerFlags')
    },
    setCustomerStats(email, stats) {
      if (!this.customerStats) this.customerStats = {}
      if (stats) {
        this.customerStats[email] = stats
        storage.setItem('CustomerStats', JSON.stringify(this.customerStats))
      } else {
        delete this.customerStats[email]
        if (Object.keys(this.customerStats).length === 0) {
          storage.removeItem('CustomerStats')
        } else {
          storage.setItem('CustomerStats', JSON.stringify(this.customerStats))
        }
      }
    },
    clearCustomerStats(email) {
      if (email && this.customerStats) {
        delete this.customerStats[email]
        if (Object.keys(this.customerStats).length === 0) {
          storage.removeItem('CustomerStats')
        } else {
          storage.setItem('CustomerStats', JSON.stringify(this.customerStats))
        }
      }
    },
    async autoUpdatePhone(ticket_id) {
      if (!this.shouldAutoUpdatePhone) return
      const data = {
        customer_dim_key: this.getCustomerDimKey,
        phone: this.getANI,
        first_name: this.getFirstName,
        last_name: this.getLastName,
        zendesk_id: this.getZendeskId
      }
      try {
        await api.put('/customers', {
          email: this.getEmail,
          call_session_id: this.getCallSessionId,
          screen_pop_id: this.getScreenPopId,
          ...(ticket_id && { ticket_id }),
          customer: data
        })
        this.customer = Object.assign({}, this.customer, { phone: this.getANI })
        storage.setItem('Customer', JSON.stringify(this.customer))
        event.success(`Phone number auto-saved: ${this.getANI}`)
      } catch (e) {
        event.error(e)
      } finally {
        this.setAskedUpdatePhone(true)
      }
    },
    clear() {
      this.setCustomer()
      this.setQuery()
      this.setScreenPopId()
      this.setRecentOrder()
      this.setRequireReasonForContact()
      this.setReasonForContact()
      this.setIdentified()
      this.setAiSummary()
      this.setSearchSelections([])
      this.setSearchResults([])
      this.setRetention()
      this.setSearchCriteria({})
      this.setSubscriptions()
      this.setSelectedSubscription()
      this.setPartnerFlags({})
      this.clearNotices()
      // AI Customer Briefing: reset context + fire-once set when the customer clears.
      this.briefingContext = null
      this.briefingShownFor = []
      storage.removeItem('BriefingShownFor')
      // Purge legacy caches (removed in phase 2 / with the orphaned /api/scripts route); older
      // sessions may still have stale entries in sessionStorage.
      storage.removeItem('CustomerScripts')
      storage.removeItem('CalloutConfigs')
      this.customerStats = {}
      storage.removeItem('CustomerStats')
    },
    logout() {
      this.clear()
      this.setContactReasons()
    }
  },
  getters: {
    getCustomer: ({ customer }) => customer,
    getCustomerDimKey: ({ customer }) => customer?.customer_dim_key,
    getName: ({ customer }) => `${customer?.first_name} ${customer?.last_name}`,
    getFirstName: ({ customer }) => customer?.first_name,
    getLastName: ({ customer }) => customer?.last_name,
    getPhone: ({ customer }) => customer?.phone,
    getEmail: ({ customer }) => customer?.email,
    getCountry: ({ customer }) => customer?.country,
    getZendeskId: ({ customer }) => Number(customer?.zendesk_id),
    getStickyId: ({ customer }) => (customer?.sticky_id && parseInt(customer.sticky_id)) || null,
    getAddress: ({ customer }) =>
      `${customer?.street || ''}${customer?.street2 ? ' ' + customer.street2 : ''}${customer?.city ? ', ' + customer.city : ''}${customer?.state || customer?.zip ? ', ' + (customer?.state || '') + ' ' + (customer?.zip || '') : ''}${customer?.country ? ', ' + customer.country : ''}`,
    getContactInfo() {
      let res = ''
      if (this.getEmail && this.getEmail != '') res += this.getEmail
      if (this.getPhone && this.getPhone != '') {
        if (res != '') res += '  -  '
        res += this.getPhone
      }
      return res
    },
    getUpsellable: ({ customer }) => customer?.upsellable,
    getSearchResults: ({ searchResults }) => searchResults,
    getSearchSelections: ({ searchSelections }) => searchSelections,
    getSearchCriteria: ({ searchCriteria }) => searchCriteria,
    getCallSessionId: ({ callSessionId }) => callSessionId,
    getChatVisitorId: ({ chatVisitorId }) => chatVisitorId,
    getChatHostname: ({ chatHostname }) => chatHostname,
    getQueue: ({ queue }) => queue,
    getANI: ({ ani }) => ani,
    getDNIS: ({ dnis }) => dnis,
    getSessionSource: ({ sessionSource }) => sessionSource,
    getRetention: ({ retention }) => retention,
    getRetentionLogId: ({ retention }) => retention?.log_id || null,
    getRequireReasonForContact: ({ requireReasonForContact }) => requireReasonForContact,
    getReasonForContact: ({ reasonForContact }) => reasonForContact,
    getIdentified: ({ identified }) => identified,
    getAiSummary: ({ aiSummary }) => aiSummary,
    getContactReasons: ({ contactReasons }) => contactReasons,
    haveContactReasons: ({ contactReasons }) => contactReasons.length > 0,
    getStickyUrl: ({ customer }) =>
      `https://${import.meta.env.VITE_STICKY_SUB_DOMAIN}.sticky.io/admin/orders.php?email_filter=${encodeURIComponent(customer?.email)}`,
    getStickyCustomerUrl: () => (sticky_id) =>
      `https://${import.meta.env.VITE_STICKY_SUB_DOMAIN}.sticky.io/admin/orders.php?show_folder=view_all&sequence=1&customer_id_filter=${sticky_id}`,
    getZendeskUrl: ({ customer }) =>
      `https://${import.meta.env.VITE_ZENDESK_SUB_DOMAIN}.zendesk.com/agent/users/${customer?.zendesk_id}/requested_tickets`,
    getZendeskUserUrl: () => (zendesk_id) =>
      `https://${import.meta.env.VITE_ZENDESK_SUB_DOMAIN}.zendesk.com/agent/users/${zendesk_id}/requested_tickets`,
    getScreenPopId: ({ screenPopId }) => screenPopId,
    getBriefingContext: ({ briefingContext }) => briefingContext,
    getSubscriptions: ({ subscriptions }) => subscriptions,
    getNotices: ({ notices }) => notices,
    getProductLineCategories: ({ productLineCategories }) => productLineCategories,
    getCategorizedSubs({ subscriptions }) {
      let final = {}
      if (subscriptions && subscriptions.length) {
        // Add existing subscriptions
        subscriptions.forEach((s) => {
          if (!final[s.product_line_category]) final[s.product_line_category] = []
          s.expanded = false
          s.payMethodLoading = false
          s.transaction_type = null
          s.initial_term = null
          s.product_size = null
          s.msrp = null
          s.cart_id = null
          s.offer_configuration_id = null
          final[s.product_line_category].push(s)
        })
      }
      // Add missing product lines to push in each product line category
      const { getSalesProductCategories } = useSalesStore()
      const products = getSalesProductCategories()
      this.getProductLineCategories.forEach((c) => {
        if (!final[c]) final[c] = []
        products[c]?.forEach((l) => {
          const hasThisProduct = final[c].some((p) => p.product_category === l.product_category)
          if (!hasThisProduct)
            final[c].push(
              reactive({
                product_line_category: l.product_line_category,
                product_line: l.product_line,
                product_category: l.product_category,
                promo_code: l.promo_code,
                status: 'Never',
                expanded: false,
                transaction_type: null,
                initial_term: null,
                product_size: null,
                msrp: null,
                price: null,
                offer_configuration_id: null,
                cart_id: null
              })
            )
        })
      })
      let ordering = {}
      let sortOrder = [
        'Active - Dunning',
        'Pending',
        'Declined',
        'Active - AR On',
        'Active - AR Off',
        'Former',
        'Never'
      ]
      for (var i = 0; i < sortOrder.length; i++) ordering[sortOrder[i]] = i
      this.getProductLineCategories.forEach((c) => {
        final[c].sort((a, b) => {
          return (
            ordering[a.status] - ordering[b.status] ||
            a.product_line.localeCompare(b.product_category)
          )
        })
      })
      return final
    },
    getAskedUpdatePhone: ({ askedUpdatePhone }) => askedUpdatePhone,
    hasLiveTech: ({ subscriptions }) =>
      subscriptions?.some(
        (s) => s.product_line_category == 'Tech Support' && s.status?.startsWith('Active')
      ),
    getCalloutEventIds: ({ calloutEventIds }) => calloutEventIds,
    getPartnerFlags: ({ partnerFlags }) => partnerFlags,
    getCustomerStats: ({ customerStats }) => customerStats || {},
    getStatsForEmail:
      ({ customerStats }) =>
      (email) =>
        customerStats?.[email] || null,
    getSelectedSubscription: ({ selectedSubscription }) => selectedSubscription,
    shouldAutoUpdatePhone: ({ customer, ani, askedUpdatePhone }) =>
      !askedUpdatePhone &&
      !!ani &&
      !!customer?.customer_dim_key &&
      normalizePhone(customer?.phone) !== normalizePhone(ani)
  }
})
